/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "../SelectMission.css";
import PropTypes from "prop-types";
import { NumericFormat } from "react-number-format";
import moment from "moment";
import { renderErrorMsg } from "../../../assets/component/renderErrorMsg.js";
import errorMsg from "../../../assets/json/errMsg.json";
import { validPaymentInfo } from "../../../assets/validators/payment.js";
import { simpleFetch } from "../../../restWrapper";
import { PLATFORM } from "../../../config";
import { getTextColor, getContrastTextColor, isLightColor, resolveThemeColors, sanitizePresetAmounts, organizationHasPayMe } from "../../../assets/helper.js";
import { getProjectTitle } from "../../../assets/projectTitleHelper.js";

class QuickPayment extends Component {
  constructor(props) {
    super(props);
    this.state = {
      generalAmount: 0,
      validGeneralAmount: false,
      selectedShortcut: null,
      selectedProjects: [],
      projectAmounts: {},
      projectAmountsTouched: {},
      showProjectSelector: false,
      transactionType: PLATFORM === 'plug' ? 'payment'
        : (props.organization && props.organization.allowedTransactionTypes === 'payment' ? 'payment' : 'donation'), // Respect org-level allowed transaction types
      coverFee:
        typeof props.coverFee === 'boolean'
          ? props.coverFee
          : false,
      generalAmountTouched: false,
      attemptedSubmit: false,
      isLoading: false, // Loading state for donate button
      feeCoverageEstimate: null,
      feeCoverageEstimateAmex: null,
      feeCoverageEstimatePayMe: null,
      feeCoverageEstimateLoading: false,
    };
    this.handleGeneralAmountChange = this.handleGeneralAmountChange.bind(this);
    this.handleShortcutClick = this.handleShortcutClick.bind(this);
    this.handleDonateClick = this.handleDonateClick.bind(this);
    this.toggleProjectSelector = this.toggleProjectSelector.bind(this);
    this.handleProjectSelection = this.handleProjectSelection.bind(this);
    this.handleProjectAmountChange = this.handleProjectAmountChange.bind(this);
    this.getTotalAmount = this.getTotalAmount.bind(this);
    this.renderDescription = this.renderDescription.bind(this);
    this.handleTransactionTypeChange = this.handleTransactionTypeChange.bind(this);
    this.handleCoverFeeToggle = this.handleCoverFeeToggle.bind(this);
    this.switchToStandardMode = this.switchToStandardMode.bind(this);
    this._feeEstimateTimer = null;
  }

  componentDidUpdate(prevProps, prevState) {
    const totalAmount = this.getTotalAmount();
    const prevTotalAmount = (() => {
      const { generalAmount, projectAmounts, selectedProjects } = prevState;
      return selectedProjects.length > 0
        ? Object.values(projectAmounts).reduce((s, a) => s + a, 0)
        : generalAmount;
    })();
    const amountChanged = totalAmount !== prevTotalAmount;
    const coverFeeChanged = this.state.coverFee !== prevState.coverFee;

    if (amountChanged || coverFeeChanged) {
      clearTimeout(this._feeEstimateTimer);
      if (this.state.coverFee && totalAmount > 0) {
        this.setState({ feeCoverageEstimateLoading: true });
        this._feeEstimateTimer = setTimeout(() => this.fetchFeeCoverageEstimate(), 400);
      } else {
        this.setState({ feeCoverageEstimate: null, feeCoverageEstimateAmex: null, feeCoverageEstimatePayMe: null, feeCoverageEstimateLoading: false });
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this._feeEstimateTimer);
  }

  async fetchFeeCoverageEstimate() {
    const totalAmount = this.getTotalAmount();
    const { organization } = this.props;
    if (!totalAmount || totalAmount <= 0 || !organization?.organizationId) {
      this.setState({ feeCoverageEstimateLoading: false });
      return;
    }
    const projectId = this.state.selectedProjects[0]?.id || undefined;
    const baseParams = {
      amount: Number(totalAmount),
      organizationId: organization.organizationId,
      ...(projectId && { projectId }),
      transactionCountry: "HK",
      cardCountry: "HK",
      paymentMethod: "creditCard",
      coverFee: true,
    };
    // PayMe carries the org's own rate and no platform service fee, so it is
    // priced separately rather than assumed to match a card (CP-281).
    const wantPayMe = organizationHasPayMe(organization);
    try {
      const [res, resAmex, resPayMe] = await Promise.all([
        simpleFetch("/user/payments/calculate-fees", "POST", baseParams),
        simpleFetch("/user/payments/calculate-fees", "POST", { ...baseParams, cardBrand: "amex" }),
        wantPayMe
          ? simpleFetch("/user/payments/calculate-fees", "POST", { ...baseParams, paymentMethod: "PayMe", cardBrand: "PayMe" })
          : Promise.resolve(null),
      ]);
      const read = (r) => (r && r.status === 200 && r.body?.totalFee != null ? r.body : null);
      this.setState({
        feeCoverageEstimate: read(res),
        feeCoverageEstimateAmex: read(resAmex),
        feeCoverageEstimatePayMe: read(resPayMe),
        feeCoverageEstimateLoading: false,
      });
    } catch (e) {
      this.setState({ feeCoverageEstimate: null, feeCoverageEstimateAmex: null, feeCoverageEstimatePayMe: null, feeCoverageEstimateLoading: false });
    }
  }

  // Fee/total span across every payment method we priced, so the range covers
  // what the donor could actually pay rather than cards alone (CP-281).
  getFeeEstimateSpan() {
    const priced = [
      this.state.feeCoverageEstimate,
      this.state.feeCoverageEstimateAmex,
      this.state.feeCoverageEstimatePayMe,
    ].filter((e) => e && e.totalFee != null && e.totalAmount != null);

    if (priced.length === 0) return null;

    const fees = priced.map((e) => Number(e.totalFee));
    const totals = priced.map((e) => Number(e.totalAmount));
    return {
      minFee: Math.min(...fees),
      maxFee: Math.max(...fees),
      minTotal: Math.min(...totals),
      maxTotal: Math.max(...totals),
      includesPayMe: !!this.state.feeCoverageEstimatePayMe,
    };
  }

  renderFeeEstimate() {
    const { feeCoverageEstimate: est, feeCoverageEstimateLoading: loading, coverFee } = this.state;
    const { languageObj } = this.props;
    if (!coverFee) return null;
    if (!est && !loading) return null;
    if (loading) {
      return (
        <div className="QuickPayment-feeEstimate" aria-busy="true" aria-live="polite">
          <div className="QuickPayment-feeEstimateRow">
            <span>{languageObj.estimatedFee || "Estimated fee"}</span>
            <span className="QuickPayment-feeEstimateSkeleton QuickPayment-feeEstimateSkeleton--short" />
          </div>
          <div className="QuickPayment-feeEstimateRow QuickPayment-feeEstimateRow--total">
            <span>{languageObj.totalCharged || "Total charged"}</span>
            <span className="QuickPayment-feeEstimateSkeleton QuickPayment-feeEstimateSkeleton--long" />
          </div>
          <div className="QuickPayment-feeEstimateNote">{languageObj.calculatingFee || "Calculating fee…"}</div>
        </div>
      );
    }
    const span = this.getFeeEstimateSpan();
    if (!span) return null;
    const money = (v) => `HK$${Number(v).toFixed(2)}`;
    const showRange = span.minFee !== span.maxFee;
    const showTotalRange = span.minTotal !== span.maxTotal;
    return (
      <div className="QuickPayment-feeEstimate">
        <div className="QuickPayment-feeEstimateRow">
          <span>{languageObj.estimatedFee || "Estimated fee"}</span>
          <span>
            {showRange
              ? `${money(span.minFee)} – ${money(span.maxFee)}`
              : money(span.minFee)}
          </span>
        </div>
        <div className="QuickPayment-feeEstimateRow QuickPayment-feeEstimateRow--total">
          <span>{languageObj.totalCharged || "Total charged"}</span>
          <span>
            {showTotalRange
              ? `${money(span.minTotal)} – ${money(span.maxTotal)}`
              : money(span.minTotal)}
          </span>
        </div>
        <div className="QuickPayment-feeEstimateNote">
          {/* Once PayMe is in the span the range crosses payment methods, not
              just card brands, so the card-brand wording no longer fits. */}
          {span.includesPayMe
            ? (languageObj.feeVariesByMethodNote || "Final fee depends on your payment method")
            : showRange
              ? (languageObj.feeCreditCardRangeNote || "Visa/Mastercard – Amex")
              : (languageObj.feeCreditCardNote || "Credit card payments only")}
        </div>
      </div>
    );
  }

  switchToStandardMode() {
    if (this.props.onSwitchToStandardMode) {
      this.props.onSwitchToStandardMode();
    }
  }

  handleTransactionTypeChange(type) {
    // Prevent changing transaction type when org restricts it
    const allowed = this.props.organization && this.props.organization.allowedTransactionTypes;
    if (allowed && allowed !== "both") {
      return;
    }
    this.setState({
      transactionType: type
    });
  }

  handleCoverFeeToggle() {
    this.setState((prevState) => ({
      coverFee: !prevState.coverFee,
    }));
  }

  getOrgIntroImageWrapperStyle() {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: '30px',
      paddingRight: '30px',
      paddingTop: '20px',
      paddingBottom: '20px',
      borderRadius: '20px',
      margin: '0 auto',
    };

    // Use orgIntroHeaderColor if available, otherwise fall back to headerColor
    const color = this.props.orgIntroHeaderColor || this.props.headerColor;
    if (!color) {
      return baseStyle;
    }

    const normalizedColor = typeof color === 'string' && color.startsWith('#')
      ? color
      : `#${color}`;

    return {
      ...baseStyle,
      backgroundColor: normalizedColor,
    };
  }

  handleGeneralAmountChange(e) {
    let amount = e.target.value;

    let amountSplit = amount.split("$");
    if (!amountSplit[1]) return false;
    amountSplit = amountSplit[1].split(",");

    let a = "";
    for (var i = 0; i < amountSplit.length; i++) {
      a += amountSplit[i];
    }

    const isAmount = validPaymentInfo.validAmount(
      a,
      this.props.minimumPaymentAmount
    );
    const numberAmount = Number(a);
    this.setState({
      generalAmount: numberAmount,
      validGeneralAmount: isAmount,
      selectedShortcut: null,
      generalAmountTouched: true,
    });
  }

  handleShortcutClick(amount) {
    this.setState({
      generalAmount: amount,
      validGeneralAmount: validPaymentInfo.validAmount(amount, this.props.minimumPaymentAmount),
      selectedShortcut: amount,
      generalAmountTouched: true,
    });
  }

  handleProjectSelection(project) {
    const { selectedProjects } = this.state;
    
    // Check if project is already selected
    const isSelected = selectedProjects.some(p => p.id === project.id);
    
    if (isSelected) {
      // Remove project from selection
      const updatedProjects = [];
      const updatedAmounts = {};
      const updatedTouched = { ...this.state.projectAmountsTouched };
      delete updatedTouched[project.id];

      // Reset to default transaction type, respecting org-level restriction
      const allowed = this.props.organization && this.props.organization.allowedTransactionTypes;
      const defaultType = (allowed === 'payment') ? 'payment' : 'donation';

      this.setState({
        selectedProjects: updatedProjects,
        projectAmounts: updatedAmounts,
        projectAmountsTouched: updatedTouched,
        transactionType: defaultType
      });
    } else {
      // Add project to selection (only one project allowed)
      // Set transaction type based on project's donation property
      const transactionType = project.donation !== false ? 'donation' : 'payment';

      this.setState({
        selectedProjects: [project], // Only store this one project
        projectAmounts: {
          [project.id]: 0
        },
        projectAmountsTouched: {
          [project.id]: false,
        },
        transactionType: transactionType // Set transaction type based on project
      });
    }
  }

  handleProjectAmountChange(projectId, e) {
    let amount = e.target.value;

    let amountSplit = amount.split("$");
    if (!amountSplit[1]) return false;
    amountSplit = amountSplit[1].split(",");

    let a = "";
    for (var i = 0; i < amountSplit.length; i++) {
      a += amountSplit[i];
    }

    const numberAmount = Number(a);
    
    this.setState({
      projectAmounts: {
        ...this.state.projectAmounts,
        [projectId]: numberAmount
      },
      projectAmountsTouched: {
        ...this.state.projectAmountsTouched,
        [projectId]: true,
      },
    });
  }

  toggleProjectSelector() {
    if (!this.props.enableProjectSelection) {
      return;
    }

    const { selectedProjects, showProjectSelector } = this.state;
    
    // If trying to hide the selector and a project is selected, don't allow it
    if (showProjectSelector && selectedProjects.length > 0) {
      // Don't hide the selector when a project is selected
      return;
    }
    
    // Otherwise, toggle as normal
    this.setState({
      showProjectSelector: !showProjectSelector
    });
  }

  getTotalAmount() {
    const { generalAmount, projectAmounts, selectedProjects } = this.state;
    
    // If projects are selected, only count project amounts
    if (selectedProjects.length > 0) {
      // Sum all project amounts
      return Object.values(projectAmounts).reduce((sum, amount) => sum + amount, 0);
    } else {
      // Otherwise, return general donation amount
      return generalAmount;
    }
  }

  isValidDonation() {
    const { generalAmount, validGeneralAmount, selectedProjects, projectAmounts } = this.state;
    const minimum = this.props.minimumPaymentAmount;

    // If projects are selected, check if any project donations are valid
    if (selectedProjects.length > 0) {
      return selectedProjects.some(project => {
        const amount = projectAmounts[project.id] || 0;
        return amount >= minimum;
      });
    } else {
      // Otherwise, check if general donation is valid
      return generalAmount >= minimum || validGeneralAmount;
    }
  }

  handleDonateClick() {
    const isValid = this.isValidDonation();

    if (!isValid) {
      this.setState({ attemptedSubmit: true });
      return;
    }

    // Prevent multiple clicks while loading
    if (this.state.isLoading) {
      return;
    }

    // Set loading state
    this.setState({ isLoading: true });

    if (this.state.attemptedSubmit) {
      this.setState({ attemptedSubmit: false });
    }

    const { generalAmount, selectedProjects, projectAmounts, transactionType } = this.state;
    const minimum = this.props.minimumPaymentAmount;
    
    // Create donations array
    const donations = [];
    
    // If projects are selected, add project donations
    if (selectedProjects.length > 0) {
      selectedProjects.forEach(project => {
        const amount = projectAmounts[project.id] || 0;
        if (amount >= minimum) {
          // Create a copy of the project with updated donation flag based on transaction type
          const updatedProject = {
            ...project,
            donation: transactionType === 'donation',
            transactionType: transactionType
          };
          
          donations.push({
            mission: updatedProject,
            amount: amount
          });
        }
      });
    } else {
      // Otherwise, add general donation/payment if amount is valid
      if (generalAmount >= minimum) {
        const mockMission = {
          id: "", // Use empty string instead of "general-donation"
          title: transactionType === 'donation'
            ? ((this.props.languageObj && this.props.languageObj.generalDonation) || "General Donation")
            : ((this.props.languageObj && this.props.languageObj.generalPayment) || "General Payment"),
          status: "unlisted",
          donation: transactionType === 'donation',
          transactionType: transactionType,
          targetAmount: 0,
          currentAmount: 0,
          description: ""
        };
        
        donations.push({
          mission: mockMission,
          amount: generalAmount
        });
      }
    }
    
    // Get the complete organization object from props
    const organization = { ...this.props.organization };
    
    // Ensure organization has all required properties
    if (!organization.paymentMethodCode) {
      // console.warn("QuickPayment - paymentMethodCode is missing in organization, using default");
      organization.paymentMethodCode = {
        // Default payment methods - all disabled by default
        alipay_hk: { code: 'alipay_hk', enabled: false },
        alipay_ch: { code: 'alipay_ch', enabled: false },
        wechatpay_hk: { code: 'wechatpay_hk', enabled: false },
        wechatpay_ch: { code: 'wechatpay_ch', enabled: false },
        bocpay: { code: 'bocpay', enabled: false },
        octopus: { code: 'octopus', enabled: false },
        fps: { code: 'fps', enabled: false },
        bank_transfer: { code: 'bank_transfer', enabled: false },
        check_writing: { code: 'check_writing', enabled: false },
        cash: { code: 'cash', enabled: false }
      };
    }
    
    // Ensure organizationId and organizationViewId are set
    if (!organization.organizationId && !organization.organizationViewId) {
      // console.warn("QuickPayment - organizationId is missing in organization");
      // If we have an id property, use it as organizationId
      if (organization.id) {
        // organizationId is the Mongo _id and organizationViewId the public
        // id, so `id` can only stand in for the second of them.
        organization.organizationId = organization._id;
        organization.organizationViewId = organization.id;
      } else {
        // console.error("QuickPayment - No organizationId available");
      }
    }
    
    if (this.props.organizationLogo) {
      organization.logo = this.props.organizationLogo;
    } else if (this.props.logoStyle && !organization.logo) {
      organization.logoStyle = this.props.logoStyle;
    }

    // Ensure header and button colors are set (for buttons/UI)
    organization.headerColor = this.props.headerColor;
    organization.buttonColor = this.props.buttonColor;

    // orgHeaderColor and orgButtonColor are already in the organization object from SelectMission
    // These preserve the actual organization colors for logo usage

    // console.log("QuickPayment - Passing complete organization object with paymentMethodCode:",
    //   JSON.stringify(organization.paymentMethodCode));
    // console.log("QuickPayment - Passing organization with organizationId:", organization.organizationId);
    
    // Get the amount to use
    let amount;
    if (donations.length === 1) {
      // If single donation, use its amount
      amount = donations[0].amount;
    } else if (donations.length > 1) {
      // If multiple donations, use first donation's amount
      // The donations array will handle the full amounts
      amount = donations[0].amount;
    } else {
      // console.error("No valid donations found");
      return;
    }

    const flowFlags = {
      isQuickPaymentFlow: true,
      ...(this.props.flowFlags || {}),
    };

    // Function to navigate to PersonalDetails
    const navigateToPersonalDetails = (donorInfo = null) => {
      this.props.history.push({
        pathname: "/PersonalDetails",
        state: {
          donations: donations,
          organization: organization,
          subdomain: this.props.subdomain,
          domain: this.props.domain,
          isCustomDomain: this.props.isCustomDomain,
          language: this.props.language,
          receipt: true,
          amount: amount, // Set amount field instead of totalAmount
          transactionType: transactionType, // Pass the transaction type
          coverFee: this.state.coverFee,
          country: "HK", // Set default country to HK (Hong Kong)
          scheduledDate: moment().toISOString(false), // Current time for immediate payment
          ...flowFlags,
          ...(donorInfo && { donorInfo }), // Add donorInfo if it exists
        }
      });
    };

    // If user is authenticated, fetch donor data first
    if (this.props.isAuth) {
      // console.log("QuickPayment - User is authenticated, fetching donor data...");
      simpleFetch("/donors", "GET")
        .then((res) => {
          if (res.status !== 200) {
            // console.log("QuickPayment - Failed to fetch donor data, status:", res.status);
            this.setState({ isLoading: false }); // Reset loading state
            navigateToPersonalDetails(); // Navigate without donor info
            return;
          }

          const dataObj = res.body;
          // console.log("QuickPayment - Received donor data:", JSON.stringify(dataObj));

          const donorInfo = {
            firstName: dataObj.firstName,
            lastName: dataObj.lastName,
            email: dataObj.username,
          };
          if (dataObj.phoneNumber) donorInfo.phoneNumber = dataObj.phoneNumber;

          // console.log("QuickPayment - Navigating with donorInfo:", JSON.stringify(donorInfo));
          // Loading state will be reset when component unmounts after navigation
          navigateToPersonalDetails(donorInfo);
        })
        .catch((error) => {
          // console.error("QuickPayment - Error fetching donor data:", error);
          this.setState({ isLoading: false }); // Reset loading state on error
          navigateToPersonalDetails(); // Navigate without donor info on error
        });
    } else {
      // User not authenticated, navigate without donor info
      // Loading state will be reset when component unmounts after navigation
      navigateToPersonalDetails();
    }
  }

  errRender(amount, shouldShow, testid) {
    const minimum = this.props.minimumPaymentAmount;

    if (!shouldShow || amount >= minimum) {
      return null;
    }
    const err = errorMsg[this.props.language].paymentErr.minimum + minimum;

    return <div data-testid={testid}>{renderErrorMsg(err)}</div>;
  }

  renderDescription(project) {
    try {
      const description = project.description;
      if (!description) {
        return null;
      }

      // Parse the description if it's a string
      const descriptionData = typeof description === 'string' 
        ? JSON.parse(description) 
        : description;

      // Get content for the current language or fallback
      let content;
      if (descriptionData.isMultiLanguage) {
        if (descriptionData[this.props.language]) {
          content = JSON.parse(descriptionData[this.props.language]);
        } else if (descriptionData.default) {
          content = JSON.parse(descriptionData.default);
        } else if (descriptionData.en) {
          content = JSON.parse(descriptionData.en);
        }
      } else if (descriptionData.default) {
        // A single-language save is still a {isMultiLanguage: false, default}
        // container, not bare Draft content.
        content = JSON.parse(descriptionData.default);
      } else {
        content = descriptionData;
      }

      if (!content || !content.blocks || !content.blocks.length) {
        return null;
      }

      // Get text from all blocks
      const text = content.blocks.map(block => block.text).join(' ');
      if (!text) {
        return null;
      }

      // Truncate text if it's too long
      const displayText = text.length > 150 ? text.substring(0, 150) + '...' : text;

      return (
        <div className="QuickPayment-projectDescription" style={{
          fontSize: '14px',
          color: '#666',
          marginTop: '8px',
          lineHeight: '1.4'
        }}>
          {displayText}
        </div>
      );
    } catch (e) {
      // console.error("Error rendering project description:", e);
      return null;
    }
  }

  renderProjectSelector() {
    const { missions } = this.props;

    if (!this.props.enableProjectSelection) {
      return null;
    }
    const { selectedProjects, projectAmounts, transactionType } = this.state;
    
    if (!this.state.showProjectSelector) {
      return null;
    }
    
    return (
      <div className="QuickPayment-projectSelector">
        <div className="QuickPayment-projectSelectorTitle">
          {transactionType === 'donation'
            ? ((this.props.languageObj && this.props.languageObj.selectProjectToDonate) || 'Select a project to donate to:')
            : ((this.props.languageObj && this.props.languageObj.selectProjectToPay) || 'Select a project to pay for:')
          }
        </div>

        <div style={{
          color: '#666',
          fontSize: '14px',
          marginBottom: '15px',
          fontStyle: 'italic'
        }}>
          {transactionType === 'donation'
            ? ((this.props.languageObj && this.props.languageObj.projectSelectionNoteDonation) || 'Note: You can select only one project at a time. When a project is selected, the transaction type will be determined by the project and the general donation option will be disabled.')
            : ((this.props.languageObj && this.props.languageObj.projectSelectionNotePayment) || 'Note: You can select only one project at a time. When a project is selected, the transaction type will be determined by the project and the general payment option will be disabled.')
          }
        </div>
        
        <div className="QuickPayment-projectList">
          {missions.map(project => {
            const isSelected = selectedProjects.some(p => p.id === project.id);
            
            return (
              <div
                key={project.id}
                data-testid={`quickpayment-project-item-${project.id}`}
                className={`QuickPayment-projectItem ${isSelected ? 'QuickPayment-projectItemSelected' : ''}`}
                onClick={() => this.handleProjectSelection(project)}
              >
                <div className="QuickPayment-projectCheckbox">
                  {isSelected && <div className="QuickPayment-projectCheckboxSelected" />}
                </div>
                <div className="QuickPayment-projectInfo">
                  <div className="QuickPayment-projectTitle">{getProjectTitle(this.props.language, project)}</div>
                  {this.renderDescription(project)}
                </div>
              </div>
            );
          })}
        </div>
        
        {selectedProjects.length > 0 && (
          <div className="QuickPayment-selectedProjects">
            <div className="QuickPayment-selectedProjectsTitle">
              Your selected project:
            </div>
            
            {selectedProjects.map(project => (
              <div key={project.id} className="QuickPayment-selectedProjectItem">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div className="QuickPayment-selectedProjectInfo">
                    <div className="QuickPayment-selectedProjectTitle">{getProjectTitle(this.props.language, project)}</div>
                  </div>

                  <div className="QuickPayment-selectedProjectAmount">
                    <NumericFormat
                      pattern="[0-9]*"
                      inputMode="decimal"
                      decimalSeparator="."
                      className="QuickPayment-amountField"
                      data-testid="quickpayment-project-amount-input"
                      valueIsNumericString={true}
                      value={projectAmounts[project.id] || 0}
                      onChange={(e) => this.handleProjectAmountChange(project.id, e)}
                      allowNegative={false}
                      thousandSeparator={true}
                      prefix={"$"}
                      fixedDecimalScale={true}
                      decimalScale={2}
                    />
                  </div>
                </div>

                {this.errRender(
                  projectAmounts[project.id] || 0,
                  this.state.projectAmountsTouched[project.id] || this.state.attemptedSubmit,
                  'quickpayment-project-amount-error'
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  render() {
    const resolved = resolveThemeColors(this.props.headerColor, this.props.buttonColor);
    const headerColor = resolved.headerColor;
    const buttonColor = resolved.buttonColor;
    const { generalAmount, selectedShortcut, selectedProjects, transactionType, coverFee } = this.state;
    const hasSelectedProjects = selectedProjects.length > 0;
    const totalAmount = this.getTotalAmount();
    const showCoverFeeToggle = totalAmount > 0;
    const showFeeBreakdown = coverFee && PLATFORM !== 'plug';

    const headerOverrideStyle = {
      background: "#" + headerColor,
    };

    const buttonOverrideStyle = {
      background: "#" + buttonColor,
    };

    const buttonReverseStyle = {
      background: "#" + buttonColor,
      color: getContrastTextColor(buttonColor, headerColor),
    }

    const headerOverrideTextStyle = {
      color: getTextColor(headerColor),
    };

    const nextBtnStyle = {
      background: "#" + headerColor,
      color: getContrastTextColor(headerColor, buttonColor),
      cursor: this.isValidDonation() ? "pointer" : "not-allowed",
      pointerEvents: this.isValidDonation() ? "auto" : "none",
      filter: this.isValidDonation() ? "brightness(100%)" : "brightness(80%)",
    };

    // Shortcut amounts — reduced to presets a donor can actually use
    // (CP-238): an invalid stored preset (empty string, below the org's
    // minimum) would render a button that fails validation silently.
    const validPresets = sanitizePresetAmounts(
      this.props.organization?.presetDonationAmounts,
      this.props.minimumPaymentAmount
    );
    const shortcutAmounts = validPresets.length > 0
      ? validPresets
      : [100, 500, 1000];

    const introDonationLabel = (this.props.languageObj && this.props.languageObj.introDonationLabel)
      ? this.props.languageObj.introDonationLabel
      : 'You are making a donation to';
    const introPaymentLabel = (this.props.languageObj && this.props.languageObj.introPaymentLabel)
      ? this.props.languageObj.introPaymentLabel
      : 'You are making a payment to';
    const introBaseLabel = transactionType === 'payment' ? introPaymentLabel : introDonationLabel;
    const introLabel = `${introBaseLabel} ${this.props.organizationName}`;

    const isFreePlanFlow = this.props.flowFlags && this.props.flowFlags.isFreePlanFlow;
    const containerClassName = isFreePlanFlow
      ? "QuickPayment-container QuickPayment-container--free"
      : "QuickPayment-container";

    // Project selector toggle button component
    const projectSelectorToggleButton = this.props.enableProjectSelection && this.props.missions && this.props.missions.length > 0 && (
      <div
        className="QuickPayment-projectSelectorToggle"
        data-testid="quickpayment-project-selector-toggle"
        style={{
          ...buttonReverseStyle,
          ...(this.state.showProjectSelector && this.state.selectedProjects.length > 0 ? {
            opacity: 0.6,
            cursor: 'not-allowed',
            position: 'relative'
          } : {})
        }}
        onClick={this.toggleProjectSelector}
      >
        <span style={buttonReverseStyle}>
          {this.state.showProjectSelector
            ? (this.state.selectedProjects.length > 0
              ? ((this.props.languageObj && this.props.languageObj.deselectProjectFirst) || "Deselect Project First")
              : ((this.props.languageObj && this.props.languageObj.hideProjects) || "Hide Projects"))
            : ((this.props.languageObj && this.props.languageObj.selectProjects) || "Select Projects")}
        </span>
        
        {/* Show tooltip when button is disabled */}
        {this.state.showProjectSelector && this.state.selectedProjects.length > 0 && (
          <div style={{
            position: 'absolute',
            bottom: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#333',
            color: '#fff',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            zIndex: 10
          }}>
            You must deselect the project first
          </div>
        )}
      </div>
    );

    return (
      <div className={containerClassName}>
        {this.props.stepsContainer}

        <div className="QuickPayment-innerContainer">
          <div className="QuickPayment-header" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {this.props.title && (
                <div className="QuickPayment-title">
                  {this.props.title}
                </div>
              )}
              {/* Switch to standard mode button - for paid users only */}
              {!isFreePlanFlow && this.props.onSwitchToStandardMode && this.props.missions && this.props.missions.length > 0 && (
                <button
                  onClick={this.switchToStandardMode}
                  style={{
                    ...buttonReverseStyle,
                    padding: '8px 15px',
                    fontSize: '14px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    border: 'none',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => e.target.style.filter = 'brightness(90%)'}
                  onMouseOut={(e) => e.target.style.filter = 'brightness(100%)'}
                >
                  {(this.props.languageObj && this.props.languageObj.viewAllProjects) || "View All Projects"}
                </button>
              )}
            </div>
            {/* Show project selector toggle in header for free users only */}
            {isFreePlanFlow && <div style={{ position: 'absolute', right: 0 }}>{projectSelectorToggleButton}</div>}
          </div>

          {/* Show project selector after header for free users only */}
          {isFreePlanFlow && this.renderProjectSelector()}

          {this.props.showOrganizationIntro && (!hasSelectedProjects || !this.props.enableProjectSelection) && (
            <div className="QuickPayment-orgIntro">
              <div className="QuickPayment-orgIntroLabel">{introLabel}</div>
              {this.props.organizationLogo ? (
                <div
                  className="QuickPayment-orgIntroImageWrapper"
                  style={this.getOrgIntroImageWrapperStyle()}
                >
                  <img
                    className="QuickPayment-orgIntroImage"
                    src={this.props.organizationLogo}
                    alt={this.props.organizationName}
                  />
                </div>
              ) : (
                <div className="QuickPayment-orgIntroName">{this.props.organizationName}</div>
              )}
            </div>
          )}

          {(!hasSelectedProjects || !this.props.enableProjectSelection) && (
            <div className="QuickPayment-generalDonation" style={{ position: 'relative' }}>
              {/* Show project selector toggle in top right for paid users */}
              {!isFreePlanFlow && (
                <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1 }}>
                  {projectSelectorToggleButton}
                </div>
              )}
              {/* Transaction Type Toggle - Hidden for plug platform or when org restricts to single type */}
              {PLATFORM !== 'plug' && (!this.props.organization || !this.props.organization.allowedTransactionTypes || this.props.organization.allowedTransactionTypes === 'both') && (
                <div style={{
                  marginBottom: '20px',
                  marginTop: this.props.showOrganizationIntro ? '10px' : '15px',
                  position: 'relative'
                }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {(this.props.languageObj && this.props.languageObj.transactionType)
                      ? this.props.languageObj.transactionType
                      : 'Transaction Type'}
                    {hasSelectedProjects && (
                      <span style={{
                        marginLeft: '10px',
                        fontSize: '12px',
                        color: '#7f7f7f',
                        fontWeight: 'normal',
                        fontStyle: 'italic'
                      }}>
                        {(this.props.languageObj && this.props.languageObj.transactionTypeSetByProject)
                          ? this.props.languageObj.transactionTypeSetByProject
                          : '(Set by project)'}
                      </span>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    borderRadius: '5px',
                    overflow: 'hidden',
                    border: '1px solid #ddd',
                    width: 'fit-content',
                    opacity: hasSelectedProjects ? 0.7 : 1
                  }}>
                    <div
                      data-testid="quickpayment-transaction-type-donation"
                      onClick={() => !hasSelectedProjects && this.handleTransactionTypeChange('donation')}
                      style={{
                        padding: '10px 20px',
                        cursor: hasSelectedProjects ? 'not-allowed' : 'pointer',
                        background: transactionType === 'donation' ? `#${headerColor}` : '#f5f5f5',
                        color: transactionType === 'donation' ? (isLightColor(headerColor) ? '#333' : '#fff') : '#333',
                        fontWeight: transactionType === 'donation' ? 'bold' : 'normal',
                        borderRight: '1px solid #ddd'
                      }}
                    >
                      {(this.props.languageObj && this.props.languageObj.donation)
                        ? this.props.languageObj.donation
                        : 'Donation'}
                    </div>
                    <div
                      data-testid="quickpayment-transaction-type-payment"
                      onClick={() => !hasSelectedProjects && this.handleTransactionTypeChange('payment')}
                      style={{
                        padding: '10px 20px',
                        cursor: hasSelectedProjects ? 'not-allowed' : 'pointer',
                        background: transactionType === 'payment' ? `#${headerColor}` : '#f5f5f5',
                        color: transactionType === 'payment' ? (isLightColor(headerColor) ? '#333' : '#fff') : '#333',
                        fontWeight: transactionType === 'payment' ? 'bold' : 'normal'
                      }}
                    >
                      {(this.props.languageObj && this.props.languageObj.payment)
                        ? this.props.languageObj.payment
                        : 'Payment'}
                    </div>
                  </div>

                  {/* Explanation text - only shown when a project is selected */}
                  {hasSelectedProjects && (
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginTop: '8px',
                      fontStyle: 'italic'
                    }}>
                      This {transactionType} type is determined by the selected project and cannot be changed.
                    </div>
                  )}

                  {/* Tooltip that appears when hovering over disabled toggle */}
                  {hasSelectedProjects && (
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #ddd',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'help',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#666'
                    }}
                    title="When a project is selected, the transaction type is determined by the project settings and cannot be changed."
                    >
                      ?
                    </div>
                  )}
                </div>
              )}

              {this.props.showOrganizationIntro ? (
                <div className="QuickPayment-inputHeading">
                  {(this.props.languageObj && this.props.languageObj.headingText)
                    ? this.props.languageObj.headingText
                    : 'Please enter an amount'}
                </div>
              ) : (
                <div className="QuickPayment-generalDonationTitle">
                  {transactionType === 'donation'
                    ? `${(this.props.languageObj && this.props.languageObj.generalDonationTo) || 'General donation to'} ${this.props.organizationName}:`
                    : `${(this.props.languageObj && this.props.languageObj.generalPaymentTo) || 'General payment to'} ${this.props.organizationName}:`
                  }
                </div>
              )}
              
              <div className="QuickPayment-inputArea">
                <div className="QuickPayment-inputContainer">
                  <div className="QuickPayment-currencySign">HKD</div>
                  <NumericFormat
                    pattern="[0-9]*"
                    inputMode="decimal"
                    decimalSeparator="."
                    className="QuickPayment-dollarField"
                    data-testid="quickpayment-amount-input"
                    valueIsNumericString={true}
                    value={generalAmount}
                    onChange={this.handleGeneralAmountChange}
                    allowNegative={false}
                    thousandSeparator={true}
                    prefix={"$"}
                    fixedDecimalScale={true}
                    decimalScale={2}
                  />
                </div>
                {this.errRender(
                  generalAmount,
                  this.state.generalAmountTouched || this.state.attemptedSubmit,
                  'quickpayment-amount-error'
                )}

                <div className="QuickPayment-shortcuts">
                  {shortcutAmounts.map((shortcutAmount) => (
                    <div
                      key={shortcutAmount}
                      data-testid={`quickpayment-shortcut-${shortcutAmount}`}
                      className={`QuickPayment-shortcutBtn ${selectedShortcut === shortcutAmount ? 'QuickPayment-shortcutSelected' : ''}`}
                      style={selectedShortcut === shortcutAmount ? headerOverrideStyle : buttonOverrideStyle}
                      onClick={() => this.handleShortcutClick(shortcutAmount)}
                    >
                      <span style={selectedShortcut === shortcutAmount ? { color: "#" + buttonColor } : headerOverrideTextStyle}>
                        ${shortcutAmount}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Show project selector below general donation for paid users - always visible when enabled */}
          {!isFreePlanFlow && (
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
              {this.renderProjectSelector()}
            </div>
          )}

          {/* Project-specific messaging intentionally hidden */}
          
          {/* Cover Fee Toggle - Hidden for plug platform */}
          {showCoverFeeToggle && PLATFORM !== 'plug' && (
            <div className="QuickPayment-coverFeeSection">
              <label className="QuickPayment-checkboxLabel">
                <input
                  type="checkbox"
                  checked={coverFee}
                  onChange={this.handleCoverFeeToggle}
                  className="QuickPayment-checkbox"
                  data-testid="quickpayment-cover-fee-checkbox"
                />
                <span className="QuickPayment-checkboxText">
                  {(this.props.languageObj && this.props.languageObj.coverTransactionFees)
                    ? this.props.languageObj.coverTransactionFees
                    : "I'll cover the transaction fees"}
                </span>
              </label>
              {coverFee && (
                <div className="QuickPayment-coverFeeNote">
                  {(this.props.languageObj && this.props.languageObj.coverFeeNote)
                    ? this.props.languageObj.coverFeeNote
                    : "We'll add the transaction fees at checkout so the nonprofit receives the full amount."}
                </div>
              )}
            </div>
          )}

          <div className="QuickPayment-totalSection">
            <div className="QuickPayment-totalLabel">
              {coverFee
                ? ((this.props.languageObj && this.props.languageObj.baseAmount)
                  ? this.props.languageObj.baseAmount
                  : 'Base Amount (before fees):')
                : ((this.props.languageObj && this.props.languageObj.totalAmount)
                  ? this.props.languageObj.totalAmount
                  : 'Total Amount:')
              }
            </div>
            <div className="QuickPayment-totalAmount">
              HK${totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>
          </div>
          {coverFee && PLATFORM !== 'plug' && this.renderFeeEstimate()}
          {coverFee && PLATFORM !== 'plug' && !showFeeBreakdown && (
            <div className="QuickPayment-totalHint">
              {(this.props.languageObj && this.props.languageObj.coverFeeHint)
                ? this.props.languageObj.coverFeeHint
                : "Your payment will include additional fees when you continue."}
            </div>
          )}
          
          <div
            className="QuickPayment-donateBtn"
            data-testid="quickpayment-donate-btn"
            style={{
              ...nextBtnStyle,
              opacity: this.state.isLoading ? 0.6 : 1,
              cursor: this.state.isLoading ? 'not-allowed' : 'pointer',
              pointerEvents: this.state.isLoading ? 'none' : 'auto'
            }}
            onClick={this.handleDonateClick}
          >
            {this.state.isLoading ? (
              <span>
                {this.props.languageObj.loading || "Loading..."}
              </span>
            ) : (
              transactionType === 'donation'
                ? (this.props.languageObj.donate || "Donate")
                : (this.props.languageObj.pay || "Pay")
            )}
          </div>
        </div>
      </div>
    );
  }
}

QuickPayment.propTypes = {
  organization: PropTypes.object.isRequired,
  organizationName: PropTypes.string.isRequired,
  headerColor: PropTypes.string.isRequired,
  orgIntroHeaderColor: PropTypes.string,
  buttonColor: PropTypes.string.isRequired,
  minimumPaymentAmount: PropTypes.number.isRequired,
  languageObj: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  subdomain: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
  isCustomDomain: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  logoStyle: PropTypes.object,
  missions: PropTypes.array.isRequired,
  enableProjectSelection: PropTypes.bool,
  title: PropTypes.string,
  coverFee: PropTypes.bool,
  showOrganizationIntro: PropTypes.bool,
  organizationLogo: PropTypes.string,
  flowFlags: PropTypes.object,
  stepsContainer: PropTypes.node,
  onSwitchToStandardMode: PropTypes.func,
  isAuth: PropTypes.bool
};

QuickPayment.defaultProps = {
  enableProjectSelection: true,
  showOrganizationIntro: false,
  flowFlags: undefined,
};

export default QuickPayment;
